/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//standard libraries
import React, { useState, useEffect } from "react";
import omitBy from "lodash-es/omitBy";
//context
import { useProgressValue } from "../context";
//custom libraries

import {
  FORM_FIELD_ORDER_DATE_FROM,
  FORM_FIELD_ORDER_DATE_TO,
  FORM_FIELD_ORDER_ID,
  FORM_FIELD_PHONE,
  LIMIT,
  ZERO_OFFSET,
} from "../constants/common";

import { useClientHandler } from "./rest/use-client-handler";

import { useProductsReportValues } from "./../context/products-search-context";
import { useProductHandler } from "./rest/use-product-handler";

export interface ParamsJson {
  limit?: number;
  offset?: number;
  [FORM_FIELD_ORDER_ID]?: string;
  findBy?: string;
  logonId?: string;
  logonIdSearchType?: string;
  orderByTableName?: string;
  orderByFieldName?: string;
  firstName?: string;
  firstNameSearchType?: string;
  lastName?: string;
  lastNameSearchType?: string;
  streetAddress1?: string;
  streetAddress1SearchType?: string;
  city?: string;
  citySearchType?: string;
  zipCode?: string;
  zipCodeSearchType?: string;
  country?: string;
  countrySearchType?: string;
  state?: string;
  stateSearchType?: string;
  email?: string;
  emailSearchType?: string;
  [FORM_FIELD_PHONE]?: string;
  phoneSearchType?: string;
  [FORM_FIELD_ORDER_DATE_FROM]?: string;
  [FORM_FIELD_ORDER_DATE_TO]?: string;
  orderDateSearchType?: string;
  addressType?: string;
  parentOrgNameSearchType?: string;
  parentOrgName?: string;
  groupBy?: String;
}

export const initParams: ParamsJson = {
  [FORM_FIELD_ORDER_DATE_FROM]: "",
  [FORM_FIELD_ORDER_DATE_TO]: "",
  groupBy: "DAY",
};

export const useProductsForm = () => {
  const { setInProgress } = useProgressValue();
  const [showMessage, setShowMessage] = useState(false);
  const [chips, setChips] = React.useState<any>({});
  const {
    params,
    setParams,
    products,
    setProducts,
    pagination,
    setPagination,
    categories,
    setCategories,
    pageNumber,
    handleIncreasePageNumber,
    handleDecreasePageNumber,
    listRecords,
    setListRecords,
    catalogs,
    setCatalogs,
  } = useProductsReportValues();
  const [showForm, setShowForm] = React.useState<boolean>(true);
  const [sortParam, setSortParam] = useState<any>();
  const { getProducts, getProductsExcel, getCategories, getCatalogs } =
    useProductHandler();

  const getPrev = () => {
    if (sortParam) {
      getProductsReport(
        sortParam,
        (products.pageNumber - 1) * LIMIT - LIMIT,
        false
      );
    } else {
      let pNumb = pageNumber - 1;
      handleDecreasePageNumber();
      getProductsReport(
        { ...params, pageNumber: pNumb },
        (products.pageNumber - 1) * LIMIT - LIMIT
      );
    }
  };

  const getNext = () => {
    handleIncreasePageNumber();
    if (sortParam) {
      getProductsReport(
        sortParam,
        LIMIT + ((products.pageNumber - 1) * LIMIT + 1),
        false
      );
    } else {
      getProductsReport(
        { ...params, pageNumber: pageNumber + 1 },
        LIMIT + ((products.recordSetStartNumber - 1) * LIMIT + 1)
      );
    }
  };

  const submitForm = (evt: any) => {
    setParams(evt);

    getProductsReport({ ...evt, pageNumber: 1 }, ZERO_OFFSET);
    getProductsForExcel({ ...evt, pageNumber: 1 }, ZERO_OFFSET);
  };

  const listCatalogCategories = (evt: any) => {
    listCategories(evt);
  };

  const getProductsReport = (param: any, newOffset: number, setChip = true) => {
    const chips = omitBy(param, (s) => String(s).trim() === "");
    let searchParams = {};
    if (chips["orderByTableName"] || chips["orderByFieldName"]) {
      const existingParams = omitBy(params, (s) => String(s).trim() === "");
      searchParams = { ...existingParams, ...chips };
    } else searchParams = { ...chips };

    setInProgress(true);
    const [res, canceler] = getProducts(searchParams, newOffset, LIMIT);

    return res
      .then((r) => r.data)
      .then((data) => {
        setProducts(data);
        setShowMessage(true);

        setPagination({
          ...pagination,
          total: data.recordSetTotal,
          records: paginate(data.resultList, 10, 1),
          numberPages: getPagesNumber(data.resultList),
        });
      })
      .catch((e) => console.log(e))
      .finally(() => {
        setInProgress(false);
      });
  };

  const getCatalogsHandler = () => {
    setInProgress(true);
    const [res, canceler] = getCatalogs();

    return res
      .then((r) => r.data)
      .then((data) => {
        if (data.resultList) {
          setCatalogs(data.resultList);
        }
      })
      .catch((e) => console.log(e))
      .finally(() => {
        setInProgress(false);
      });
  };

  const getProductsForExcel = (
    param: any,
    newOffset: number,
    setChip = true
  ) => {
    const chips = omitBy(param, (s) => String(s).trim() === "");
    let searchParams = {};
    if (chips["orderByTableName"] || chips["orderByFieldName"]) {
      const existingParams = omitBy(params, (s) => String(s).trim() === "");
      searchParams = { ...existingParams, ...chips };
    } else searchParams = { ...chips };

    const [res, canceler] = getProductsExcel(searchParams);

    return res
      .then((r) => r.data)
      .then((data) => {
        setListRecords(data);
      })
      .catch((e) => console.log(e));
  };

  const listCategories = (param: any) => {
    const chips = omitBy(param, (s) => String(s).trim() === "");
    let searchParams = {};
    if (chips["orderByTableName"] || chips["orderByFieldName"]) {
      const existingParams = omitBy(params, (s) => String(s).trim() === "");
      searchParams = { ...existingParams, ...chips };
    } else searchParams = { ...chips };

    setInProgress(true);
    const [res, canceler] = getCategories(searchParams);

    return res
      .then((r) => r.data)
      .then((data) => {
        setCategories(data);
        console.log(data);
        setShowMessage(true);
      })
      .catch((e) => console.log(e))
      .finally(() => {
        setInProgress(false);
      });
  };

  const removeChip = (name: string) => {
    const newParams = { ...params, [name]: "" };
    /*  getSales(newParams, ZERO_OFFSET); */
    setParams(newParams);
  };

  function paginate(array: any, page_size: number, page_number: number) {
    // human-readable page numbers usually start with 1, so we reduce 1 in the first argument
    return array.slice((page_number - 1) * page_size, page_number * page_size);
  }

  function getPagesNumber(array: any) {
    return Math.ceil(array.length / LIMIT);
  }

  useEffect(() => {
    getCatalogsHandler();
  }, []);

  return {
    /*     changeInput, */
    listCatalogCategories,
    listRecords,
    submitForm,
    removeChip,
    chips,
    pagination,
    showForm,
    setShowForm,
    products,
    showMessage,
    setShowMessage,
    getNext,
    getPrev,
    categories,
    catalogs,
  };
};
